import { GiftCardActions } from '../actions'
import type { ButtonProps } from '../components/button/button'
import { handleError } from '../handle-error'
import type { GiftCardProps } from '../modules/gift-card/gift-card'
import { Storage } from '../storage'
import type { GiftCardView, LangStrings, ValidationError } from '../types'
import { getGiftCardPaymentSection } from './payment-section'

export const getGiftCardProps = (view: GiftCardView): GiftCardProps | undefined => {
    const { state, update } = view
    const { lang, data, validationErrors } = state

    if (!data) {
        return undefined
    }

    const { ticketPrice, minAmount, showAmountPresetButtons, labels, paymentFailed } = data

    const getButton = (count: number, pattern: string): ButtonProps => {
        const amount = String(Math.round(count * ticketPrice * 100) / 100)

        return {
            text: pattern.replace('{price}', amount.replace('.', ',')),
            onClick: () => {
                state.amount = amount
                update()
            },
        }
    }

    const props: GiftCardProps = {
        theme: state.theme,
        isLoading: state.isLoading,
        title: labels.title,
        amountPresets: {
            text: lang.giftCard_amountPresets_text,
            buttons: [
                getButton(1, lang.giftCard_oneTicket),
                getButton(2, lang.giftCard_twoTickets),
                getButton(6, lang.giftCard_sixTickets),
                getButton(10, lang.giftCard_tenTickets),
            ],
        },
        showAmountPresetButtons,
        customAmount: {
            text: lang.giftCard_customAmount,
            input: {
                id: 'amount',
                value: state.amount,
                onChange: (value) => {
                    state.amount = value
                    update()
                },
                hiddenLabel: true,
                error: getAmountError(lang, validationErrors.amount, minAmount),
            },
        },
        note: labels.note,
        email: {
            input: {
                id: 'email',
                label: lang.giftCard_email_label,
                value: state.email,
                onChange: (value) => {
                    state.email = value
                    update()
                },
                error: validationErrors.email ? lang.contact_email_invalid : undefined,
            },
            note: labels.emailNote,
        },
        submit: {
            text: lang.submit_pay,
            onClick: async () => {
                try {
                    await GiftCardActions.submit(view)
                } catch (error) {
                    handleError(view, error as Error)
                }
            },
        },
    }
    if (state.data) {
        if (state.data.paymentOptions.length > 1) {
            props.paymentSection = getGiftCardPaymentSection(view)
        } else {
            Storage.update({ selectedPaymentGateway: state.data.paymentOptions[0].name })
        }
    }

    if (paymentFailed) {
        props.error = {
            title: lang.error_title,
            message: lang.banks_paymentFailed,
        }
    }

    return props
}

const getAmountError = (
    lang: LangStrings,
    error: ValidationError | undefined,
    min: number,
): string | undefined => {
    if (!error) {
        return
    }

    switch (error) {
        case 'min':
            return lang.giftCard_amount_min.replace('{min}', String(min).replace('.', ','))
        case 'max':
            return lang.giftCard_amount_max
        default:
            return lang.giftCard_amount_invalid
    }
}
